
import { useEffect, useState } from 'react';
import { Check, TriangleAlert } from 'lucide-react';

import type { ApiError } from '../../api/client';
import { useVoiceSample } from '../../hooks';
import {
  Card,
  EmptyState,
  Pill,
  PlayButton,
  SearchInput,
  Seg,
  SkeletonRows,
  type AudioPreview,
  type SegOption,
} from '../../components/ui';
import styles from './TtsPage.module.css';
import { VOICE_GROUPS, type VoiceGroup, type VoiceOption } from './voiceOptions';

interface Props {
  options: VoiceOption[];
  visible: VoiceOption[];
  counts: Record<VoiceGroup, number>;
  group: VoiceGroup | 'all';
  onGroup: (group: VoiceGroup | 'all') => void;
  query: string;
  onQuery: (query: string) => void;
  selectedKey: string | null;
  onSelect: (option: VoiceOption) => void;
  isLoading: boolean;
  catalogError: ApiError | null;
  fallbackToLive: boolean;
  audio: AudioPreview;
}

export function VoicePicker({
  options,
  visible,
  counts,
  group,
  onGroup,
  query,
  onQuery,
  selectedKey,
  onSelect,
  isLoading,
  catalogError,
  fallbackToLive,
  audio,
}: Props) {
  const segOptions: SegOption<VoiceGroup | 'all'>[] = [
    { value: 'all', label: `Все ${options.length}` },
    ...VOICE_GROUPS.map((item) => ({
      value: item.key,
      label: `${item.short} ${counts[item.key]}`,
    })),
  ];

  return (
    <Card className={styles.picker}>
      <div className={styles.pickerHead}>
        <div className={styles.pickerHeadRow}>
          <span className="eyebrow">голос</span>
          <span className={styles.pickerCount}>{options.length}</span>
        </div>

        <SearchInput
          value={query}
          onChange={onQuery}
          placeholder="Поиск по имени, языку, тембру"
          ariaLabel="Поиск голоса"
        />

        <Seg value={group} onChange={onGroup} options={segOptions} ariaLabel="Группы голосов" />
      </div>

      <div className={styles.voiceList}>
        {isLoading && (
          <div className={styles.pad}>
            <SkeletonRows rows={4} />
          </div>
        )}

        {!isLoading && options.length === 0 && (
          <EmptyState
            compact
            title="Голосов нет"
            description={
              catalogError
                ? catalogError.code === 'provider_not_configured'
                  ? 'Озвучка не настроена, обратитесь к администратору.'
                  : catalogError.status === 503
                    ? 'Провайдер озвучки не отвечает. Попробуйте через несколько минут.'
                    : 'Список голосов не загрузился. Обновите страницу.'
                : 'Голосов пока нет. Заведите свой на экране «Клонирование голоса» или возьмите готовый ниже.'
            }
          />
        )}

        {!isLoading && options.length > 0 && visible.length === 0 && (
          <EmptyState
            compact
            title="Ничего не найдено"
            description={
              query.trim()
                ? `Ничего не нашлось по «${query.trim()}». Очистите поиск или смените группу.`
                : 'В этой группе голосов нет, переключитесь на «Все».'
            }
          />
        )}

        {!isLoading && catalogError && options.length > 0 && (
          <p className={styles.noteRow}>
            <TriangleAlert size={13} strokeWidth={1.5} className={styles.noteIcon} />
            <span>
              {catalogError.code === 'provider_not_configured'
                ? 'Озвучка не настроена: показаны только ваши голоса.'
                : catalogError.status === 503
                  ? 'Провайдер озвучки не отвечает: показаны только ваши голоса.'
                  : 'Список голосов не загрузился: показаны только ваши.'}
            </span>
          </p>
        )}

        {!isLoading && fallbackToLive && (
          <p className={styles.noteRow}>
            <TriangleAlert size={13} strokeWidth={1.5} className={styles.noteIcon} />
            <span>
              Список голосов ещё не обновлялся, он подтянется сам.
            </span>
          </p>
        )}

        {visible.map((option) => (
          <VoiceRow
            key={option.key}
            option={option}
            selected={option.key === selectedKey}
            onSelect={onSelect}
            audio={audio}
          />
        ))}
      </div>
    </Card>
  );
}

function VoiceRow({
  option,
  selected,
  onSelect,
  audio,
}: {
  option: VoiceOption;
  selected: boolean;
  onSelect: (option: VoiceOption) => void;
  audio: AudioPreview;
}) {
  const [sampleRequested, setSampleRequested] = useState(false);
  const needsSample = !option.previewUrl && Boolean(option.profileId);
  const sample = useVoiceSample(
    needsSample ? (option.profileId ?? undefined) : undefined,
    sampleRequested,
  );

  const playing = audio.playingKey === option.key;
  const canPlay = Boolean(option.previewUrl) || needsSample;
  const { toggle, stop } = audio;
  const sampleUrl = sample.data?.url;

  const handlePlay = () => {
    if (playing) {
      stop();
      return;
    }
    const src = option.previewUrl ?? sampleUrl ?? null;
    if (src) {
      toggle(option.key, src);
      return;
    }
    if (needsSample) setSampleRequested(true);
  };

  useEffect(() => {
    if (!sampleRequested || !sampleUrl) return;
    setSampleRequested(false);
    toggle(option.key, sampleUrl);
  }, [sampleRequested, sampleUrl, toggle, option.key]);

  const rowClass = selected
    ? styles.voiceRowActive
    : option.problem
      ? styles.voiceRowBroken
      : styles.voiceRow;

  return (
    <div className={rowClass}>
      <PlayButton
        size="sm"
        playing={playing}
        loading={sample.isFetching}
        disabled={!canPlay}
        onClick={handlePlay}
        title={
          canPlay
            ? playing
              ? 'Остановить'
              : 'Прослушать образец'
            : 'Образца нет: голос ещё не клонирован'
        }
        ariaLabel={playing ? 'Остановить образец' : `Прослушать образец: ${option.name}`}
      />

      <button
        type="button"
        className={styles.voiceBody}
        onClick={() => onSelect(option)}
        aria-pressed={selected}
      >
        <span className={styles.voiceName}>
          <span className={styles.voiceNameText}>{option.name}</span>
          <Pill variant={option.group === 'mine' ? 'solid' : 'soft'}>
            {option.group === 'mine' ? 'клон' : option.group === 'pro' ? 'проф' : 'станд'}
          </Pill>
        </span>
        <span className={option.problem ? styles.voiceProblem : styles.voiceMeta}>
          {option.problem ?? option.meta}
        </span>
      </button>

      {selected && <Check size={14} strokeWidth={2} className={styles.check} />}
    </div>
  );
}
